import * as React from 'react';
import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';
import isNil from 'lodash/isNil';
import snakeCase from 'lodash/snakeCase';

import { ModalFrame, ModalFrameFooter } from 'components/modal';
import { Form, FormField } from 'components/form';
import Input from 'components/inputs/input/Input';
import ActionButton from 'components/buttons/action-button/ActionButton';

import CampaignPurchaseOrderContext from 'modules/campaigns/components/purchase-order/context/CampaignPurchaseOrderContext';

import { __t, applyTranslations } from 'utils/language';
import { maxLength } from 'utils/validators';
import { MAX_CHAR_COUNTS } from 'modules/campaigns/components/purchase-order/utils/campaign-purchase-order';

import './campaign-purchase-order-item-modal.scss';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./campaign-purchase-order-item-modal.i18n.json'));

import type { TextEntry } from 'types';
import type {
  CampaignPurchaseOrderItem,
  CampaignPurchaseOrderItemIdentifier
} from 'modules/campaigns/components/purchase-order/types';

interface Props {
  identifier: CampaignPurchaseOrderItemIdentifier,
  isEditMode: boolean,
  onCloseModal: () => void
}

const COMMON_MSG_KEY = 'collabary_common';
const MSG_KEY = 'campaign_purchase_order_item_modal';

const CampaignPurchaseOrderItemModal: React.FC<Props> = ({
  identifier,
  isEditMode,
  onCloseModal
}): JSX.Element => {
  const context = React.useContext(CampaignPurchaseOrderContext);
  const item = context?.purchaseOrder[identifier] ?? {} as CampaignPurchaseOrderItem;

  const handleFormSubmit = (): void => {
    if (isFunction(context?.onModalButtonClick)) {
      context?.onModalButtonClick(identifier);
    }

    onCloseModal();
  };

  const handleFieldChange = (
    event: React.SyntheticEvent<TextEntry>
  ): void => {
    const target = event.currentTarget;
    const name = target.name as CampaignPurchaseOrderItemIdentifier;
    const value = target.value;

    if (!isNil(name) && isFunction(context?.onFieldChange)) {
      context?.onFieldChange(name, value);
    }
  };

  const renderButtons = (): React.ReactNode => (
    <React.Fragment>
      <ActionButton
        color="tertiary"
        onClick={onCloseModal}
      >
        {__t(`${COMMON_MSG_KEY}.action_button.cancel`)}
      </ActionButton>
      <ActionButton
        type="submit"
        color="primary"
        disabled={isEmpty(item.value)}
        data={{ identifier }}
      >
        {__t(`${COMMON_MSG_KEY}.action_button.save`)}
      </ActionButton>
    </React.Fragment>
  );

  const renderField = (): React.ReactNode => (
    <FormField
      showValidationIcon={false}
      direction="column"
      validators={[
        maxLength(item.value, MAX_CHAR_COUNTS[item.identifier])
      ]}
    >
      <Input
        name={item.identifier}
        value={item.value}
        autoComplete="off"
        placeholder={__t(`${MSG_KEY}.placeholders.${snakeCase(item.identifier)}`)}
        width="responsive"
        focusOnMount
        charCount={{
          curCount: (item.value || '').length,
          maxChars: MAX_CHAR_COUNTS[item.identifier]
        }}
        onChange={handleFieldChange}
      />
    </FormField>
  );

  const title = __t(`${MSG_KEY}.titles.${snakeCase(item.identifier)}`, {
    mode: __t(`${MSG_KEY}.labels.${isEditMode ? 'edit' : 'add'}`)
  });

  return (
    <div className="campaign-purchase-order-item-modal">
      <Form onSubmit={handleFormSubmit}>
        <ModalFrame
          title={title}
          classNameFrame="campaign-purchase-order-item-modal__frame"
        >
          <div className="campaign-purchase-order-item-modal__inner">
            {renderField()}
          </div>
        </ModalFrame>
        <ModalFrameFooter className="campaign-purchase-order-item-modal__footer">
          {renderButtons()}
        </ModalFrameFooter>
      </Form>
    </div>
  );
};

export default CampaignPurchaseOrderItemModal;
